// src/utils/token.ts
import type { MqttConnector, UserInfo } from '@/types/auth'

class TokenManager {
  private static hasStorage(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined'
  }

  // 检查 token 是否过期
  static isTokenExpired(): boolean {
    if (!this.hasStorage()) return true
    const expiresAt = localStorage.getItem('tokenExpiresAt')
    if (!expiresAt) return true

    const expireTime = parseInt(expiresAt, 10)
    // 提前5分钟过期，避免在请求中过期
    return Date.now() > expireTime - 5 * 60 * 1000
  }

  // 获取 token
  static getToken(): string | null {
    if (!this.hasStorage()) return null
    const token = localStorage.getItem('token')
    if (!token || this.isTokenExpired()) {
      this.clearToken()
      return null
    }
    return token
  }

  // 新增：获取token剩余时间
  static getTokenTimeLeft(): number {
    if (!this.hasStorage()) return 0
    const expiresAt = localStorage.getItem('tokenExpiresAt')
    if (!expiresAt) return 0
    const expireTime = parseInt(expiresAt, 10)
    return expireTime - Date.now()
  }

  // 新增：获取设备ID
  static getDeviceId(): string | null {
    if (!this.hasStorage()) return null
    return localStorage.getItem('deviceId')
  }

  // 新增：获取refreshToken
  static getRefreshToken(): string | null {
    if (!this.hasStorage()) return null
    return localStorage.getItem('refreshToken')
  }

  // 清除所有认证信息
  static clearToken(): void {
    if (!this.hasStorage()) return
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('tokenExpiresAt')
    localStorage.removeItem('user')
    localStorage.removeItem('remember')
    localStorage.removeItem('mqttConnector')
  }

  static saveConnector(connector: MqttConnector): void {
    if (!this.hasStorage()) return
    localStorage.setItem('mqttConnector', JSON.stringify(connector))
  }

  static getConnector(): MqttConnector | null {
    if (!this.hasStorage()) return null
    const raw = localStorage.getItem('mqttConnector')
    if (!raw) return null
    try {
      return JSON.parse(raw) as MqttConnector
    } catch {
      return null
    }
  }

  // 保存登录信息
  static saveLoginData(data: {
    accessToken: string
    refreshToken: string
    expiresIn?: number
    deviceId?: string
    connector?: MqttConnector
  }): void {
    if (!this.hasStorage()) return
    localStorage.setItem('token', data.accessToken)
    localStorage.setItem('refreshToken', data.refreshToken)

    if (data.expiresIn) {
      const expiresAt = Date.now() + data.expiresIn * 1000
      localStorage.setItem('tokenExpiresAt', expiresAt.toString())
    }

    if (data.deviceId) {
      localStorage.setItem('deviceId', data.deviceId)
    }

    if (data.connector) {
      this.saveConnector(data.connector)
    }
  }

  // 检查登录状态
  static isLoggedIn(): boolean {
    return !!this.getToken()
  }

  // 获取用户信息
  static getUserInfo(): any {
    if (!this.hasStorage()) return null
    const userStr = localStorage.getItem('user')
    return userStr ? JSON.parse(userStr) : null
  }

  // 更新用户信息（connector 单独存 mqttConnector，不写入 user）
  static updateUserInfo(userInfo: UserInfo): void {
    if (!this.hasStorage()) return
    const { connector: _connector, ...profile } = userInfo
    localStorage.setItem('user', JSON.stringify(profile))
  }
}

export default TokenManager